import { useState } from "react";
import { Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import EmptyState from "@/components/common/EmptyState";
import SectionCard from "@/components/common/SectionCard";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetDealEmails } from "@/hooks/useDeals";
import { cn } from "@/utils/common.utils";
import { formatActivityTimestamp } from "@/utils/deals-view.utils";
import { emailStatusTone } from "@/utils/email-log.utils";

const TONE_CLASS = {
  good: "bg-primary/10 text-primary",
  bad: "bg-dash-rust/10 text-dash-rust",
  muted: "bg-dash-searchBg text-dash-muted",
};

/** Every email Stoop sent on this deal — who, what, when, and whether it arrived. */
export default function EmailLogList({ dealId }: { dealId: string }) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useGetDealEmails(dealId, page);
  const emails = data?.emails ?? [];
  const totalPages = data?.pagination.totalPages ?? 1;

  // Rows shaped like the real list, so the card keeps its height when the answer arrives.
  if (isLoading) {
    return (
      <SectionCard className="overflow-hidden" contentClassName="p-0">
        <div role="status" aria-label="Loading emails" data-testid="email-log-loading">
          {[0, 1, 2].map((row) => (
            <div key={row} className={cn("flex items-center gap-4 px-4 py-4 sm:px-6", row > 0 && "border-t border-dash-border")}>
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-4 w-3/5 max-w-full" />
                <Skeleton className="h-3 w-2/5 max-w-full" />
              </div>
              <Skeleton className="h-6 w-20 shrink-0 rounded-full" />
            </div>
          ))}
          <span className="sr-only">Loading emails…</span>
        </div>
      </SectionCard>
    );
  }
  if (isError && !data) {
    return (
      <EmptyState
        compact
        icon={Mail}
        title="Couldn’t load emails"
        description="Check your connection and try again."
        action={<Button type="button" variant="dashboardOutline" size="sm" className="mt-4" onClick={() => refetch()}>Try again</Button>}
      />
    );
  }
  if (!emails.length) {
    return <EmptyState icon={Mail} title="No emails yet" description="Emails Stoop sends on this deal will appear here." />;
  }
  return (
    <SectionCard className="overflow-hidden" contentClassName="p-0">
      <ul data-testid="email-log">
        {emails.map((email, index) => (
          <li
            key={email.id}
            className={cn(
              "flex flex-col gap-1 px-4 py-4 sm:flex-row sm:items-center sm:gap-4 sm:px-6",
              index > 0 && "border-t border-dash-border"
            )}
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-dash-ink">{email.subject || email.kind_label}</p>
              <p className="mt-0.5 truncate text-xs text-dash-faint">
                {email.kind_label} · to {email.to} · {formatActivityTimestamp(email.created_at)}
              </p>
              {email.reason && <p className="mt-0.5 text-xs text-dash-muted">{email.reason}</p>}
            </div>
            <span
              className={cn(
                "w-fit shrink-0 rounded-full px-2.5 py-1 text-xs font-medium",
                TONE_CLASS[emailStatusTone(email.status, email.status_label)]
              )}
            >
              {email.status_label}
            </span>
          </li>
        ))}
      </ul>
      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 border-t border-dash-border px-4 py-3">
          <Button variant="dashboardOutline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <Button variant="dashboardOutline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
            Next
          </Button>
        </div>
      )}
    </SectionCard>
  );
}
